import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

const MODES = [
  ["customer_pickup", "Customer Self Pickup"], ["company_vehicle", "Company Vehicle"],
  ["third_party", "Third-Party Vehicle"], ["porter", "Porter / Delivery Service"],
  ["part_load", "Part Load"], ["full_load", "Full Load"],
];
const ESC_REASONS = [
  ["customer_urgent", "Customer urgently requires material"], ["commitment_approaching", "Customer commitment approaching"],
  ["dispatch_delay", "Dispatch delay"], ["picking_delay", "Picking delay"], ["qc_delay", "QC delay"],
  ["packing_delay", "Packing delay"], ["einvoice_delay", "E-Invoice delay"], ["vehicle_delay", "Vehicle delay"],
  ["loading_delay", "Loading delay"], ["stock_issue", "Stock issue"], ["delivery_delay", "Delivery delay"], ["other", "Other"],
];

export function OwnerBanner({ owner }) {
  if (!owner) return null;
  return (
    <div className="bg-primary/10 border border-primary/30 rounded-lg px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="owner-banner">
      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Current owner</span>
      <span className="font-bold text-primary uppercase text-sm">{owner.team}</span>
      {owner.persons?.length > 0 && <span className="text-sm">({owner.persons.join(", ")})</span>}
      <span className="text-sm">→ Next: <strong>{owner.next_action}</strong></span>
      {owner.task_id && <span className="metric text-xs text-muted-foreground">{owner.task_id} · {owner.task_status}</span>}
    </div>
  );
}

export function WorkflowPanels({ order, fs, isManager, isSales, viewerRole, onChanged }) {
  const [escOpen, setEscOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [esc, setEsc] = useState({ reason: "", priority: "medium", comments: "" });
  const [cycle, setCycle] = useState("30");

  const call = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); onChanged?.(); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Action failed"); }
  };

  const einv = order.documents?.einvoice;

  return (
    <div className="space-y-3" data-testid="workflow-panels">
      <div className="flex flex-wrap gap-2">
        {fs === "ready_for_dispatch" && ["admin", "warehouse_manager", "supervisor", "dispatch", "dispatch_manager"].includes(viewerRole) && (
          <Select onValueChange={(m) => call(() => api.post(`/orders/v2/${order.bill_no}/delivery-mode`, { mode: m }), "Delivery mode set")}>
            <SelectTrigger className="w-64" data-testid="delivery-mode-select"><SelectValue placeholder="Select delivery mode…" /></SelectTrigger>
            <SelectContent>{MODES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {order.delivery_mode && (
          <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1.5 rounded bg-accent text-foreground self-center" data-testid="delivery-mode-badge">
            {MODES.find(([v]) => v === order.delivery_mode)?.[1] || order.delivery_mode}
          </span>
        )}
        {isSales && !["closed", "draft"].includes(fs) && (
          <Button size="sm" variant="outline" className="text-signal-amber border-signal-amber/40" onClick={() => setEscOpen(true)} data-testid="raise-escalation-button">
            <AlertTriangle className="w-4 h-4 mr-1" /> Raise Escalation
          </Button>)}
        {isManager && ["feedback_done", "customer_issue"].includes(fs) && (
          <Button size="sm" className="bg-signal-green hover:bg-signal-green/90 text-white" onClick={() => setCloseOpen(true)} data-testid="close-order-button">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Close Order
          </Button>)}
      </div>

      {einv && (
        <div className="bg-card border border-border rounded-lg p-3 text-xs space-y-1" data-testid="einvoice-card">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">E-Invoice (Tally, uploaded by {einv.uploaded_by})</p>
          <p className="metric">Invoice {einv.invoice_no} {einv.invoice_date && `· ${einv.invoice_date}`} {einv.irn && `· IRN ${einv.irn}`} {einv.eway_bill && `· EWB ${einv.eway_bill}`}</p>
          {einv.document_path && (
            <a className="text-primary underline" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${einv.document_path}`} target="_blank" rel="noreferrer" data-testid="einvoice-download">View E-Invoice document</a>)}
        </div>
      )}

      {(order.communications || []).length > 0 && (
        <div className="bg-card border border-border rounded-lg p-3 text-xs space-y-1" data-testid="communications-card">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Customer Communication</p>
          {order.communications.map((c) => (
            <p key={`${c.at}-${c.by}-${c.method}`} className="metric">{(c.at || "").slice(0, 16).replace("T", " ")} · {c.by} · shared via <strong>{c.method}</strong>{c.remarks ? ` — ${c.remarks}` : ""}</p>
          ))}
        </div>
      )}

      {order.customer_feedback && (
        <div className={`border rounded-lg p-3 text-xs ${order.customer_feedback.option === "everything_ok" ? "bg-signal-green/10 border-signal-green/30" : "bg-signal-red/10 border-signal-red/30"}`} data-testid="feedback-card">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Customer Feedback</p>
          <p className="font-semibold uppercase">{order.customer_feedback.option.replace(/_/g, " ")}</p>
          {order.customer_feedback.remarks && <p className="text-muted-foreground">{order.customer_feedback.remarks}</p>}
        </div>
      )}

      <Dialog open={escOpen} onOpenChange={setEscOpen}>
        <DialogContent className="bg-card border-border" data-testid="escalation-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Raise Escalation — {order.bill_no}</DialogTitle><DialogDescription className="sr-only">Raise an operational escalation for this order</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Reason *</Label>
              <Select value={esc.reason} onValueChange={(v) => setEsc({ ...esc, reason: v })}>
                <SelectTrigger data-testid="esc-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>{ESC_REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Priority</Label>
              <Select value={esc.priority} onValueChange={(v) => setEsc({ ...esc, priority: v })}>
                <SelectTrigger data-testid="esc-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "medium", "high", "critical"].map((p) => <SelectItem key={p} value={p} className="uppercase">{p}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Comments</Label><Textarea value={esc.comments} onChange={(e) => setEsc({ ...esc, comments: e.target.value })} data-testid="esc-comments" /></div>
            <Button className="w-full bg-signal-amber hover:bg-signal-amber/90 text-white font-bold"
              onClick={() => call(async () => { await api.post("/escalations", { bill_no: order.bill_no, ...esc }); setEscOpen(false); }, "Escalation raised")}
              data-testid="esc-submit">RAISE ESCALATION</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="bg-card border-border" data-testid="close-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Close Order — {order.bill_no}</DialogTitle><DialogDescription className="sr-only">Confirm order closure and reorder nurture cycle</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Closure verifies: fulfilment complete, documentation done, feedback received, no open exceptions/escalations.</p>
            <div><Label className="text-xs">Expected reorder cycle (days)</Label><Input type="number" value={cycle} onChange={(e) => setCycle(e.target.value)} data-testid="close-cycle" /></div>
            <Button className="w-full bg-signal-green hover:bg-signal-green/90 text-white font-bold"
              onClick={() => call(async () => { await api.post(`/orders/v2/${order.bill_no}/close`, { reorder_cycle_days: parseInt(cycle) || 30 }); setCloseOpen(false); }, "Order closed — repeat-order nurture created")}
              data-testid="close-submit">CONFIRM CLOSURE</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
